import { useState, useCallback } from "react";
import StoreModal from "./modal/StoreMod";
import { Plus } from "react-bootstrap-icons";
import { formatCurrency } from "../utilities/formatCurrency";
import { motion } from "framer-motion";

const ProductItem = ({
  id,
  name,
  description,
  imageSrc,
  price,
  category,
  storeId,
  type,
  subType,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = useCallback(() => setShowModal(false), []);
  const handleShow = useCallback(() => setShowModal(true), []);

  const handleOpenModal = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      handleShow();
    } catch (err) {
      console.error("Error opening modal:", err);
      setError("Failed to open product details");
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, handleShow]);

  const imgHeight = category === "liquor" ? 190 : 148;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.25 }}
      style={{
        backgroundColor: "#fff",
        borderRadius: 16,
        border: "1px solid rgba(15,23,42,0.07)",
        boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
        overflow: "hidden",
        cursor: isLoading ? "wait" : "pointer",
        opacity: isLoading ? 0.72 : 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <StoreModal
        productId={id}
        name={name}
        description={description}
        price={price}
        imageSrc={imageSrc}
        showModal={showModal}
        handleClose={handleClose}
        storeId={storeId}
        category={category}
        type={type}
        subType={subType}
      />

      {/* Image */}
      <div
        onClick={handleOpenModal}
        style={{
          height: imgHeight,
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <img
          src={imageSrc}
          alt={name}
          style={{
            width: "100%", height: "100%",
            objectFit: "contain",
            transition: "transform 0.22s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.04)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
          onError={(e) => {
            e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
            e.target.onerror = null;
          }}
        />
      </div>

      {/* Content */}
      <div style={{
        flex: 1, padding: "12px 14px 14px",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ flex: 1 }}>
          <h5 style={{
            fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e",
            margin: "0 0 3px",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {name}
          </h5>
          <p style={{
            fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)",
            margin: 0, lineHeight: 1.4,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {description || "No description available"}
          </p>
        </div>

        {/* Price + Add */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginTop: 12,
        }}>
          <span style={{ fontSize: "1rem", fontWeight: 700, color: "#1c1c1e" }}>
            {formatCurrency(price)}
          </span>

          <motion.button
            whileTap={{ scale: 0.88 }}
            onClick={handleOpenModal}
            disabled={isLoading}
            style={{
              width: 34, height: 34, borderRadius: "50%",
              backgroundColor: "#007aff",
              border: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: isLoading ? "wait" : "pointer",
              flexShrink: 0,
            }}
          >
            <Plus size={19} color="#fff" />
          </motion.button>
        </div>

        {error && (
          <div style={{ marginTop: 8, fontSize: "0.75rem", color: "#ff3b30" }}>
            {error}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProductItem;
